import { Switch, useToast } from '@repo/design-system/index'
import { QIcon_chevron_right } from '@repo/icons/index'
import classNames from 'classnames'
import { differenceInMonths, format, isValid, subMonths } from 'date-fns'
import { useEffect, useState } from 'react'
import { CAF_ANALYSIS_TYPE_MAP, SummaryLessonItem, SummaryLessonsResponse } from '../../../../../model/caf.interface'
import { useCafSummaryLessonSettingV5, useCafSummaryLessonsV5 } from '../../../../../modules/apis/webview/caf'
import { APP_SCREEN_NAME } from '../../../../../modules/hooks/common/appRouterVars'
import useAppRouter from '../../../../../modules/hooks/common/useAppRouter'
import useOffsetDate from '../../../../../modules/hooks/webview/caf/common/useOffsetDate'
import useRNMsg from '../../../../../modules/hooks/webview/common/useRNMsg'
import useReturnFullUrl from '../../../../../modules/hooks/webview/common/useReturnFullUrl'
import { ai_analytics, n_common } from '../../../../../modules/i18n/strings/auto/wording'
import { n_caf } from '../../../../../modules/i18n/strings/auto/wording_webview'
import useTranslation from '../../../../../modules/i18n/useTranslation'
import { ICON_BOOK_OPEN_GRAY600, ICON_TUTOR_INACTIVE } from '../../../../../modules/svg/svg'
import urls from '../../../../../modules/urls'
import { COLORS } from '../../../../../modules/vars'
import { useLessonsStack } from '../../../../../pages/[lang]/webview/lessons'
import Badge from '../../../../common/Badge'
import LoadingSpinner from '../../../../common/LoadingSpinner'
import FilterSection from '../../../../new-portal/caf/statistic/FilterSection'
import WebviewStackContainer from '../../../common/WebviewStackContainer'
type SortOption = {
  label: string
  value: number
  name: string
}
const AIStatisticLessonListActivity = ({ params }) => {
  const { useLessonsFlow } = useLessonsStack()
  const { pop, push } = useLessonsFlow()
  const router = useAppRouter()
  const { initialStartDate, initialEndDate } = params
  const { t } = useTranslation()
  const today = new Date()
  const dateWOffset = useOffsetDate()
  const { toastSuccess, toastError } = useToast()
  const defaultStartDate = dateWOffset(subMonths(today, 1))
  const defaultEndDate = today
  const getFullUrl = useReturnFullUrl()
  const { sendPostMsg } = useRNMsg()
  const options: SortOption[] = [
    { label: t(n_caf.statistic.date_chip1), value: 1, name: 'one_month' },
    { label: t(n_caf.statistic.date_chip2), value: 3, name: 'three_month' },
    { label: t(n_caf.statistic.date_chip3), value: 6, name: 'six_month' },
    { label: t(n_caf.statistic.date_chip4), value: 12, name: 'one_year' },
    { label: t(n_caf.statistic.date_title), value: 0, name: 'custom' },
  ]

  const [startDate, setStartDate] = useState(
    isValid(new Date(initialStartDate)) ? new Date(initialStartDate) : defaultStartDate
  )
  const [endDate, setEndDate] = useState(isValid(new Date(initialEndDate)) ? new Date(initialEndDate) : defaultEndDate)
  const [lessons, setLessons] = useState<SummaryLessonItem[]>()
  const [summaryLessons, setSummaryLessons] = useState<Partial<SummaryLessonsResponse>>()
  const monthDiff = differenceInMonths(endDate, startDate)
  const [timePeriod, setTimePeriod] = useState<number>(monthDiff)
  const [edit, setEdit] = useState(false)
  const handleEdit = () => {
    setEdit((prev) => !prev)
  }
  const { data, isLoading, refetch, fetchNextPage, isFetchingNextPage } = useCafSummaryLessonsV5(
    format(startDate, 'yyyy-MM-dd'),
    format(endDate, 'yyyy-MM-dd')
  )
  const { mutate } = useCafSummaryLessonSettingV5(refetch)

  useEffect(() => {
    if (data?.pages?.length) {
      setLessons(
        data.pages.reduce((prev: SummaryLessonItem[], after: SummaryLessonsResponse) => {
          return prev.concat(after.lessons)
        }, []) as SummaryLessonItem[]
      )
      setSummaryLessons(data.pages[data.pages.length - 1])
      return
    }
  }, [data?.pages])

  const handleSummary = (id: number, status: boolean) => {
    return () => {
      if (lessons.filter((item) => item.allow_summary)?.length < 2 && !status) {
        toastError(t(n_caf.statistic.lesson_list.unavailable))
        return
      }
      if (lessons) {
        mutate({ id, status })
        if (status) {
          toastSuccess(t(n_caf.statistic.lesson_list.on))
        } else {
          toastSuccess(t(n_caf.statistic.lesson_list.off))
        }
      }
    }
  }

  const navOverview = (id, title) => () => {
    if (edit) return
    router.push('', {
      appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
      appSendData: { url: getFullUrl(urls.webview.lessons.review.detailAsType(id, 'caf')) },
    })
  }

  return (
    <div>
      <WebviewStackContainer
        height="52px"
        onBack={pop}
        title={t(ai_analytics.title)}
        border={true}
        appendRight={() => (
          <div className="fixed right-[17px] z-[999]" onClick={handleEdit}>
            {edit ? (
              <span className="text-14 text-gray-700">{t(n_caf.highlight_list_edit.done)}</span>
            ) : (
              <span className="text-14 text-gray-700">{t(n_caf.statistic.less_list.edit)}</span>
            )}
          </div>
        )}
      >
        {isLoading || !lessons ? (
          <div className="h-screen">
            <LoadingSpinner lg primary show />
          </div>
        ) : (
          <>
            <div className="mt-[20px] mb-[14px] flex w-full px-[20px]">
              <FilterSection
                timePeriod={timePeriod}
                setTimePeriod={setTimePeriod}
                startDate={startDate}
                endDate={endDate}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
              />
            </div>
            <div className="mt-20 px-20">
              <div className="mb-16 text-[14px]">{t(n_caf.statistic.less_list.count(summaryLessons?.count))}</div>
              {lessons?.map((item) => (
                <div
                  key={item.lesson_id}
                  className={styles.container(edit || item.allow_summary)}
                  onClick={!item.allow_summary ? null : navOverview(item.lesson_id, item.material)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex">
                      <div className={styles.time(edit || item.allow_summary)}>
                        {item.formatted_start_date} {item.formatted_start_time}
                      </div>
                      {item.duration === 20 ? (
                        <Badge twentyLight clx="pt-[2px]">
                          {t(n_common.label_20min)}
                        </Badge>
                      ) : (
                        <Badge fortyLight clx="pt-[2px]">
                          {t(n_common.label_40min)}
                        </Badge>
                      )}
                    </div>
                    {edit ? (
                      <Switch
                        checked={item.allow_summary}
                        setChecked={handleSummary(item.caf_setting_id, !item.allow_summary)}
                      />
                    ) : (
                      <QIcon_chevron_right width={12} height={12} color={COLORS.GRAY600} />
                    )}
                  </div>
                  <div className="mt-[13px] flex items-center">
                    <img src={ICON_TUTOR_INACTIVE} width={16} height={16} alt="tutor" className="mr-8" />
                    <div className={styles.title(edit || item.allow_summary)}>{item.tutor_first_name}</div>
                  </div>
                  <div className="mt-[4px] mb-12 flex items-center">
                    <img src={ICON_BOOK_OPEN_GRAY600} width={16} height={16} alt="tutor" className="mr-8" />
                    <div className={styles.title(edit || item.allow_summary)}>{item.material}</div>
                  </div>
                  <ul className={styles.bottom.box}>
                    {/* CAF Level은 3차 베타에서 삭제, 추후 다시 도입될 예정 */}
                    {Object.entries(item.levels).map(([key, value]) => (
                      <li className={styles.bottom.item} key={key}>
                        <h2 className={styles.bottom.title(edit || item.allow_summary)}>
                          {key === CAF_ANALYSIS_TYPE_MAP[0] ? 'CAF Level' : key}
                        </h2>
                        <div className="flex flex-row items-center justify-start">
                          <span className={styles.bottom.count(edit || item.allow_summary)}>
                            {key === CAF_ANALYSIS_TYPE_MAP[0] ? Math.floor(value) : Math.floor(value)}
                          </span>
                          <div className="flex items-center justify-center rounded-[4px] bg-white px-6 py-2 text-[14px] text-gray-400">
                            {value}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              {summaryLessons?.next && (
                <div className="flex justify-center py-[16px]">
                  <button className={styles.button} onClick={() => fetchNextPage()}>
                    {isFetchingNextPage ? <LoadingSpinner primary show md /> : '더보기'}
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </WebviewStackContainer>
    </div>
  )
}

export default AIStatisticLessonListActivity
const styles = {
  bottom: {
    box: `flex items-center justify-around rounded bg-gray-50 py-[8px]`,
    item: `not-first:border-l flex-1 border-gray-300 px-[12px]`,
    title: (active: boolean) =>
      classNames(
        `text-h6 mb-[4px] font-medium capitalize leading-[18px] text-gray-700`,
        active ? `text-black` : `text-gray-500`
      ),
    count: (active: boolean) =>
      classNames(`text-sTitle mr-8 font-bold text-purple-500`, active ? `text-purple-500` : `text-gray-500`),
  },
  container: (active: boolean) =>
    classNames(`mb-[16px] rounded border p-[16px] shadow-sm`, active ? `bg-white` : `bg-gray-50`),
  time: (active: boolean) => classNames(`text-14 mr-8 font-bold`, active ? `text-black` : `text-gray-500`),
  title: (active: boolean) => classNames(`text-h6 line-clamp-1`, active ? `text-black` : `text-gray-500`),
  button: `flex h-[42px] w-[164px] flex-col items-center justify-center rounded-[4px] border border-purple-500 text-purple-500`,
}
